import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const Home = () => {
  const mountRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // ---- Scene setup ----
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0.4, 7);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    // ---- Lights ----
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.9);
    keyLight.position.set(3, 4, 5);
    scene.add(keyLight);
    const warmLight = new THREE.PointLight(0xd98a3d, 1.4, 12);
    warmLight.position.set(-3, -1, 3);
    scene.add(warmLight);

    // ---- Central "patient" node: a sculpted 3D heart ----
    const buildHeartShape = () => {
      const shape = new THREE.Shape();
      const scale = 0.052;
      const segments = 120;
      const pts = [];
      for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        const x = 16 * Math.pow(Math.sin(t), 3);
        const y =
          13 * Math.cos(t) -
          5 * Math.cos(2 * t) -
          2 * Math.cos(3 * t) -
          Math.cos(4 * t);
        pts.push(new THREE.Vector2(x * scale, y * scale));
      }
      shape.setFromPoints(pts);
      return shape;
    };

    const coreGeo = new THREE.ExtrudeGeometry(buildHeartShape(), {
      depth: 0.42,
      bevelEnabled: true,
      bevelThickness: 0.07,
      bevelSize: 0.07,
      bevelSegments: 8,
      curveSegments: 48,
    });
    coreGeo.center();
    coreGeo.computeVertexNormals();

    const coreMat = new THREE.MeshPhysicalMaterial({
      color: 0xc23b4a,
      emissive: 0x4a0f14,
      emissiveIntensity: 0.35,
      roughness: 0.28,
      metalness: 0.1,
      clearcoat: 0.6,
      clearcoatRoughness: 0.3,
      side: THREE.DoubleSide,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.scale.setScalar(1.55);
    scene.add(core);

    // ---- Orbit rings of "specialists" ----
    const disposables = [coreGeo, coreMat];
    const orbitGroups = [];

    const ringConfigs = [
      { radius: 2.1, tiltX: 0.5, tiltZ: 0.05, speed: 0.16, color: 0xd98a3d, count: 2 },
      { radius: 2.65, tiltX: -0.35, tiltZ: 0.55, speed: -0.11, color: 0x74b8b3, count: 3 },
      { radius: 3.15, tiltX: 0.15, tiltZ: -0.65, speed: 0.085, color: 0xe0a35c, count: 2 },
    ];

    ringConfigs.forEach((cfg) => {
      const group = new THREE.Group();
      group.rotation.x = cfg.tiltX;
      group.rotation.z = cfg.tiltZ;

      const ringGeo = new THREE.TorusGeometry(cfg.radius, 0.008, 8, 96);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xbfe3dd,
        transparent: true,
        opacity: 0.35,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      group.add(ring);
      disposables.push(ringGeo, ringMat);

      for (let i = 0; i < cfg.count; i++) {
        const angle = (i / cfg.count) * Math.PI * 2;
        const nodeGeo = new THREE.SphereGeometry(0.16, 20, 20);
        const nodeMat = new THREE.MeshStandardMaterial({
          color: cfg.color,
          emissive: cfg.color,
          emissiveIntensity: 0.25,
          roughness: 0.4,
          metalness: 0.1,
        });
        const node = new THREE.Mesh(nodeGeo, nodeMat);
        node.position.set(
          Math.cos(angle) * cfg.radius,
          0,
          Math.sin(angle) * cfg.radius
        );
        group.add(node);
        disposables.push(nodeGeo, nodeMat);
      }

      scene.add(group);
      orbitGroups.push({ group, speed: cfg.speed });
    });

    // ---- Resize handling (container-aware) ----
    const resize = () => {
      const { clientWidth, clientHeight } = mount;
      if (!clientWidth || !clientHeight) return;
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    // ---- Pointer parallax ----
    const pointer = { x: 0, y: 0 };
    const targetRotation = { x: 0, y: 0 };
    const handlePointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      targetRotation.y = pointer.x * 0.18;
      targetRotation.x = pointer.y * 0.12;
    };
    mount.addEventListener('pointermove', handlePointerMove);

    // ---- Animation loop ----
    let frameId;
    const clock = new THREE.Clock();

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      const elapsed = clock.getElapsedTime();

      if (!prefersReducedMotion) {
        orbitGroups.forEach(({ group, speed }) => {
          group.rotation.y += speed * delta;
        });

        // Gentle sway so the heart's sculpted face stays readable
        core.rotation.y = Math.sin(elapsed * 0.35) * 0.28;
        core.rotation.x = 0.1 + Math.sin(elapsed * 0.5) * 0.05;

        // "Lub-dub" heartbeat: two quick bumps per cycle, then rest
        const period = 1.15;
        const phase = (elapsed % period) / period;
        const bump = (center, width) =>
          Math.exp(-Math.pow((phase - center) / width, 2));
        const beat = 1 + 0.09 * bump(0.05, 0.045) + 0.05 * bump(0.24, 0.05);
        core.scale.setScalar(1.55 * beat);

        scene.rotation.y += (targetRotation.y - scene.rotation.y) * 0.04;
        scene.rotation.x += (targetRotation.x - scene.rotation.x) * 0.04;
      }

      renderer.render(scene, camera);
    };
    animate();

    // ---- Cleanup ----
    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mount.removeEventListener('pointermove', handlePointerMove);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="cr-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        :root {
          --cr-bg: #F6F9F8;
          --cr-panel: #FFFFFF;
          --cr-ink: #0E2A2C;
          --cr-ink-soft: #4C6664;
          --cr-teal: #1B6E6B;
          --cr-teal-deep: #124F4C;
          --cr-gold: #C97A2E;
          --cr-mint: #E3F1EE;
          --cr-line: rgba(14, 42, 44, 0.1);
        }

        .cr-page * { box-sizing: border-box; }
        .cr-page {
          background: var(--cr-bg);
          color: var(--cr-ink);
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
        }
        .cr-mono {
          font-family: 'IBM Plex Mono', monospace;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* ---- Nav ---- */
        .cr-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px clamp(20px, 5vw, 64px);
          border-bottom: 1px solid var(--cr-line);
          position: relative;
          z-index: 10;
        }
        .cr-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: 'Fraunces', serif;
          font-size: 21px;
          font-weight: 600;
          color: var(--cr-teal-deep);
        }
        .cr-logo-mark {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 1.5px solid var(--cr-teal);
          position: relative;
          flex-shrink: 0;
        }
        .cr-logo-mark::after {
          content: '';
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--cr-gold);
          top: 3px;
          right: 2px;
        }
        .cr-logo-mark::before {
          content: '';
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--cr-teal);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .cr-links {
          display: flex;
          gap: 34px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .cr-links a {
          color: var(--cr-ink-soft);
          text-decoration: none;
          font-size: 14.5px;
          font-weight: 500;
          transition: color 0.2s ease;
        }
        .cr-links a:hover, .cr-links a:focus-visible { color: var(--cr-teal-deep); }

        .cr-nav-actions { display: flex; align-items: center; gap: 18px; }
        .cr-link-plain {
          color: var(--cr-ink-soft);
          text-decoration: none;
          font-size: 14.5px;
          font-weight: 500;
        }
        .cr-btn {
          font-family: 'Inter', sans-serif;
          font-size: 14.5px;
          font-weight: 600;
          border-radius: 8px;
          padding: 11px 20px;
          border: none;
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }
        .cr-btn-primary {
          background: var(--cr-teal-deep);
          color: #fff;
        }
        .cr-btn-primary:hover { background: var(--cr-teal); transform: translateY(-1px); }
        .cr-btn-ghost {
          background: transparent;
          color: var(--cr-teal-deep);
          border: 1.5px solid var(--cr-line);
        }
        .cr-btn-ghost:hover { border-color: var(--cr-teal); transform: translateY(-1px); }
        .cr-btn:focus-visible, .cr-links a:focus-visible, .cr-link-plain:focus-visible {
          outline: 2px solid var(--cr-teal);
          outline-offset: 2px;
        }

        .cr-burger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
        }
        .cr-burger span {
          width: 22px;
          height: 2px;
          background: var(--cr-ink);
          border-radius: 2px;
        }

        /* ---- Hero ---- */
        .cr-hero {
          display: grid;
          grid-template-columns: 1.05fr 1fr;
          align-items: center;
          gap: 40px;
          padding: clamp(40px, 7vw, 80px) clamp(20px, 5vw, 64px) clamp(60px, 8vw, 100px);
          max-width: 1360px;
          margin: 0 auto;
        }
        .cr-eyebrow {
          font-size: 12px;
          color: var(--cr-teal);
          font-weight: 500;
          margin-bottom: 18px;
        }
        .cr-eyebrow::before {
          content: '';
          display: inline-block;
          width: 16px;
          height: 1px;
          background: var(--cr-teal);
          margin-right: 10px;
          vertical-align: middle;
        }
        .cr-hero h1 {
          font-family: 'Fraunces', serif;
          font-weight: 500;
          font-size: clamp(38px, 4.6vw, 60px);
          line-height: 1.06;
          letter-spacing: -0.01em;
          margin: 0 0 22px;
          color: var(--cr-teal-deep);
        }
        .cr-hero h1 em {
          font-style: italic;
          color: var(--cr-gold);
        }
        .cr-hero p {
          font-size: 17px;
          line-height: 1.65;
          color: var(--cr-ink-soft);
          max-width: 460px;
          margin: 0 0 32px;
        }
        .cr-hero-actions {
          display: flex;
          gap: 14px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }
        .cr-hero-actions .cr-btn { padding: 13px 24px; font-size: 15px; }

        .cr-trust {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: var(--cr-ink-soft);
        }
        .cr-trust-stars { color: var(--cr-gold); letter-spacing: 1px; }

        /* ---- 3D stage ---- */
        .cr-stage {
          position: relative;
          height: clamp(320px, 42vw, 520px);
        }
        .cr-canvas-mount {
          position: absolute;
          inset: 0;
        }
        .cr-canvas-mount canvas { display: block; width: 100% !important; height: 100% !important; }

        .cr-chip {
          position: absolute;
          background: var(--cr-panel);
          border: 1px solid var(--cr-line);
          border-radius: 12px;
          padding: 12px 16px;
          box-shadow: 0 8px 24px rgba(14, 42, 44, 0.08);
          animation: cr-float 5s ease-in-out infinite;
        }
        .cr-chip-num {
          font-family: 'Fraunces', serif;
          font-size: 20px;
          color: var(--cr-teal-deep);
          font-weight: 600;
          margin-bottom: 2px;
        }
        .cr-chip-label {
          font-size: 11.5px;
          color: var(--cr-ink-soft);
        }
        .cr-chip--one { top: 6%; left: -2%; animation-delay: 0s; }
        .cr-chip--two { bottom: 10%; right: -2%; animation-delay: 1.4s; }

        @keyframes cr-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        /* ---- How it works ---- */
        .cr-section {
          padding: clamp(50px, 7vw, 90px) clamp(20px, 5vw, 64px);
          max-width: 1360px;
          margin: 0 auto;
        }
        .cr-section-head { max-width: 560px; margin-bottom: 44px; }
        .cr-section-head h2 {
          font-family: 'Fraunces', serif;
          font-weight: 500;
          font-size: clamp(28px, 3vw, 38px);
          color: var(--cr-teal-deep);
          margin: 10px 0 0;
        }

        .cr-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
        }
        .cr-step {
          background: var(--cr-panel);
          border: 1px solid var(--cr-line);
          border-radius: 16px;
          padding: 28px 26px;
        }
        .cr-step-index {
          font-family: 'IBM Plex Mono', monospace;
          color: var(--cr-gold);
          font-size: 13px;
          margin-bottom: 16px;
        }
        .cr-step h3 {
          font-family: 'Fraunces', serif;
          font-weight: 500;
          font-size: 19px;
          margin: 0 0 10px;
          color: var(--cr-ink);
        }
        .cr-step p {
          font-size: 14.5px;
          line-height: 1.6;
          color: var(--cr-ink-soft);
          margin: 0;
        }

        /* ---- Specialties ---- */
        .cr-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .cr-pill {
          display: flex;
          align-items: center;
          gap: 9px;
          background: var(--cr-mint);
          border-radius: 999px;
          padding: 10px 18px;
          font-size: 14px;
          font-weight: 500;
          color: var(--cr-teal-deep);
        }
        .cr-pill-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--cr-gold);
        }

        /* ---- CTA band ---- */
        .cr-cta-band {
          margin: 0 clamp(20px, 5vw, 64px) 0;
          max-width: 1360px;
          margin-left: auto;
          margin-right: auto;
          background: var(--cr-teal-deep);
          border-radius: 24px;
          padding: clamp(40px, 6vw, 64px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }
        .cr-cta-band h2 {
          font-family: 'Fraunces', serif;
          font-weight: 500;
          color: #fff;
          font-size: clamp(24px, 3vw, 32px);
          margin: 0 0 8px;
        }
        .cr-cta-band p { color: rgba(255,255,255,0.7); margin: 0; font-size: 14.5px; }
        .cr-cta-band .cr-btn-primary { background: var(--cr-gold); }
        .cr-cta-band .cr-btn-primary:hover { background: #b56b26; }

        /* ---- Footer ---- */
        .cr-footer {
          padding: 40px clamp(20px, 5vw, 64px) 50px;
          max-width: 1360px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          color: var(--cr-ink-soft);
          flex-wrap: wrap;
          gap: 14px;
        }

        @media (max-width: 900px) {
          .cr-links, .cr-nav-actions .cr-link-plain { display: none; }
          .cr-burger { display: flex; }
          .cr-hero { grid-template-columns: 1fr; }
          .cr-stage { order: -1; height: 320px; }
          .cr-steps { grid-template-columns: 1fr; }
          .cr-hero p { max-width: 100%; }
        }

        @media (prefers-reduced-motion: reduce) {
          .cr-chip { animation: none; }
        }
      `}</style>

      {/* Nav */}
      <nav className="cr-nav">
        <div className="cr-logo">
          <span className="cr-logo-mark" />
          Curon
        </div>
        <ul className="cr-links">
          <li><a href="#doctors">Find Doctors</a></li>
          <li><a href="#specialties">Specialties</a></li>
          <li><a href="#how">How It Works</a></li>
          <li><a href="#providers">For Providers</a></li>
        </ul>
        <div className="cr-nav-actions">
          <a href="#login" className="cr-link-plain">Log in</a>
          <button className="cr-btn cr-btn-primary">Get Started</button>
        </div>
        <button
          className="cr-burger"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* Hero */}
      <header className="cr-hero">
        <div>
          <p className="cr-mono cr-eyebrow">Coordinated care</p>
          <h1>
            Every specialist your health needs, <em>orbiting</em> one plan.
          </h1>
          <p>
            Curon matches you with the right doctor in minutes, keeps your
            records synced across every visit, and makes sure your whole
            care team is reading from the same page.
          </p>
          <div className="cr-hero-actions">
            <button className="cr-btn cr-btn-primary">Find your doctor</button>
            <button className="cr-btn cr-btn-ghost">See how it works</button>
          </div>
          <div className="cr-trust">
            <span className="cr-trust-stars">★★★★★</span>
            <span>4.9 average · 12,400 patients this year</span>
          </div>
        </div>

        <div className="cr-stage">
          <div className="cr-canvas-mount" ref={mountRef} />
          <div className="cr-chip cr-chip--one">
            <div className="cr-chip-num">500+</div>
            <div className="cr-chip-label">Verified specialists</div>
          </div>
          <div className="cr-chip cr-chip--two">
            <div className="cr-chip-num">24/7</div>
            <div className="cr-chip-label">On-call triage</div>
          </div>
        </div>
      </header>

      {/* How it works */}
      <section className="cr-section" id="how">
        <div className="cr-section-head">
          <p className="cr-mono" style={{ color: 'var(--cr-teal)', fontSize: 12 }}>
            How it works
          </p>
          <h2>Three steps from symptom to specialist.</h2>
        </div>
        <div className="cr-steps">
          <div className="cr-step">
            <div className="cr-step-index">01</div>
            <h3>Tell us what's going on</h3>
            <p>A short intake helps us understand your symptoms, history, and urgency.</p>
          </div>
          <div className="cr-step">
            <div className="cr-step-index">02</div>
            <h3>We match you with a specialist</h3>
            <p>Our triage matches you to a verified doctor in the right specialty, near you.</p>
          </div>
          <div className="cr-step">
            <div className="cr-step-index">03</div>
            <h3>Meet online or in person</h3>
            <p>Book instantly, join a video visit or an in-clinic slot, and get your plan.</p>
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section className="cr-section" id="specialties">
        <div className="cr-section-head">
          <p className="cr-mono" style={{ color: 'var(--cr-teal)', fontSize: 12 }}>
            Specialties
          </p>
          <h2>Whatever it is, there's a doctor for it.</h2>
        </div>
        <div className="cr-pills">
          {['Cardiology', 'Dermatology', 'Pediatrics', 'Mental Health', 'Orthopedics', 'General Practice', 'ENT', 'Gynecology'].map((s) => (
            <div className="cr-pill" key={s}>
              <span className="cr-pill-dot" />
              {s}
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section style={{ padding: '0 clamp(20px, 5vw, 64px) clamp(60px, 8vw, 90px)' }}>
        <div className="cr-cta-band">
          <div>
            <h2>Ready when you are.</h2>
            <p>Create your account and book your first visit in under five minutes.</p>
          </div>
          <button className="cr-btn cr-btn-primary">Get Started — it's free</button>
        </div>
      </section>

      <footer className="cr-footer">
        <span>© {new Date().getFullYear()} Curon. All rights reserved.</span>
        <span>Privacy · Terms · Contact</span>
      </footer>
    </div>
  );
};

export default Home;